import { FunctionComponent, useCallback, type JSX } from 'react';
import { useDispatch } from 'react-redux';

import Modal from '@payaca/components/modal/Modal';

import Button from '@payaca/components/button/Button';
import { ButtonStyleVariant } from '@payaca/components/button/enums';
import * as jobsStoreActions from '@payaca/store/jobs/jobsActions';

import { useSelector } from '@/api/state';
import { ErrorMessage } from '@payaca/components/feedbackMessage/FeedbackMessage';
import { JobType } from '@payaca/types/jobTypes';
import './MarkAsSentModal.css';

type Props = {
  isOpen: boolean;
  jobId: number;
  jobType?: JobType;
  onClose: () => void;
  markAsSentCallback?: () => void;
};

const MarkAsSentModal: FunctionComponent<Props> = ({
  isOpen,
  jobId,
  onClose,
  markAsSentCallback,
}: Props): JSX.Element => {
  const dispatch = useDispatch();

  const isMarkingJobAsSent = useSelector((state) => {
    return state.jobsStore.isMarkingJobAsSent;
  });

  const markJobAsSentError = useSelector((state) => {
    return state.jobsStore.markJobAsSentErrorMessage;
  });

  const onModalClose = () => {
    dispatch(jobsStoreActions.clearMarkJobAsSent());
    onClose();
  };

  const onMarkAsSent = useCallback(() => {
    dispatch(
      jobsStoreActions.requestMarkJobAsSent(jobId, () => {
        markAsSentCallback?.();
        onModalClose();
      })
    );
  // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React hook useCallback depends on `onModalClose`, which changes every render
  }, [jobId, dispatch, markAsSentCallback, onModalClose]);

  return (
    <Modal
      isOpen={isOpen}
      title="Mark as sent"
      size="xs"
      onClose={onModalClose}
      className="mark-as-sent-modal"
      actions={
        <>
          <Button
            onClick={onMarkAsSent}
            iconAfter={"chevron-right"}
            isProcessing={isMarkingJobAsSent}
            styleVariant={ButtonStyleVariant.OUTSIZE}
          >
            Mark as sent without sending
          </Button>
          {markJobAsSentError && <ErrorMessage message={markJobAsSentError} />}
        </>
      }
    >
      <p className="text-center">
        An email won&apos;t be sent but automations will still run.
      </p>
    </Modal>
  );
};

export default MarkAsSentModal;
